import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

/** How recently a truck's tracker has reported, against how often it should (gitaumoses4/chukua#86). */
export const TRACKER_HEALTH_STATUSES = ['healthy', 'late', 'silent'] as const;
export type TrackerHealthStatus = (typeof TRACKER_HEALTH_STATUSES)[number];

/**
 * Placeholders until a person signs off the real numbers (gitaumoses4/chukua#86): a tracker is late after 3 missed intervals and silent
 * after 10, and a push-only adapter, which has no polling interval to expect, is assumed to report every minute. The API's tracker-health
 * policy and the live map's status colours both read them, so the two cannot disagree. The API passes them into its policy rather than
 * reading them inside, so moving them to company settings later changes the callers only.
 */
export const TRACKER_HEALTH_DEFAULTS = {
  lateAfterIntervals: 3,
  silentAfterIntervals: 10,
  pushOnlyExpectedIntervalSeconds: 60,
} as const;

/** `status` sorts by how many intervals have passed since the last event, so descending puts the quietest trackers first. */
export const trackerHealthListQuerySchema = defineListQuery({
  sortFields: ['lastEventAt', 'deviceId', 'status'],
  defaultSort: { sort: 'status', order: 'desc' },
  filters: {
    status: listFilter(z.enum(TRACKER_HEALTH_STATUSES)),
    integrationId: listFilter(z.uuid()),
  },
});
export type TrackerHealthListQuery = z.infer<typeof trackerHealthListQuerySchema>;

/**
 * Devices an installation has heard from, for the truck form's picker (gitaumoses4/chukua#422). A device on a truck is left out,
 * except on `forTruckId`'s own, so the truck being edited still sees the device it carries.
 */
export const reportingDeviceListQuerySchema = defineListQuery({
  sortFields: ['deviceId', 'lastHeardAt'],
  defaultSort: { sort: 'lastHeardAt', order: 'desc' },
  filters: {
    integrationId: listFilter(z.uuid()),
    forTruckId: z.uuid().optional(),
  },
});
export type ReportingDeviceListQuery = z.infer<typeof reportingDeviceListQuerySchema>;

export interface ReportingDevice {
  integrationId: string;
  integrationName: string;
  deviceId: string;
  lastHeardAt: string;
}
